import useOperationalClock from '../core/shifts/useOperationalClock';
import useTenantTimeZone from '../components/common/useTenantTimeZone';
import { resolveShiftTimeZone, regionalCalendarBounds } from '../core/shifts/shiftTimeZone';
import React, { useEffect, useMemo, useState } from "react";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";

import useFacilityProfile from "../core/data/useFacilityProfile";
import useFacilityShifts from "../core/data/useFacilityShifts";
import useFacilityWorkers from "../core/data/useFacilityWorkers";
import { ShiftBridgeAssist } from "../components/ShiftBridgeAssist";
import { buildShiftMetrics } from "../utils/shiftMetricsEngine";
import {
  getShiftOperationalSummary,
} from "../core/shifts/shiftOperationalSummary";
import { buildFacilityFinancialMetrics } from "../utils/facilityFinancialMetricsEngine";
import { buildCredentialExpirationSummary } from "../utils/credentialExpirationEngine";

const TABS = [
  ["executive", "Executive"],
  ["workforce", "Workforce"],
  ["shifts", "Shifts"],
  ["financial", "Financial"],
  ["alerts", "Alerts"],
];

const SCOPES = [
  ["today", "Today"],
  ["tomorrow", "Tomorrow"],
  ["week", "Week"],
  ["month", "Month"],
  ["history", "History"],
];

const CLOSED_STATUSES = [
  "cancelled",
  "canceled",
  "no_show",
  "closed_unfilled",
];

function resolveAssignedWorkerName(shift, assignedWorkerUid, workers) {
  const worker = assignedWorkerUid
    ? workers.find((row) => String(row.uid || row.id) === String(assignedWorkerUid))
    : null;
  return shift.assignedWorkerName || shift.workerName || shift.assignedWorkerDisplayName ||
    worker?.fullName || worker?.name || worker?.displayName || worker?.email || "";
}

function money(value) {
  return `$${Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 0,
  })}`;
}

function toMillis(value) {
  if (!value) return NaN;

  if (typeof value?.toDate === "function") {
    return value.toDate().getTime();
  }

  return new Date(value).getTime();
}

function normalizeRole(shift) {
  return String(
    shift?.requiredPosition ||
      shift?.role ||
      shift?.workerType ||
      shift?.position ||
      shift?.primaryRole ||
      shift?.licenseType ||
      ""
  )
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
}

function isInScope(shift, scope, nowMs = Date.now(), timeZone) {
  if (!timeZone) return false;
  const startMs = toMillis(shift?.startTime || shift?.start);
  const endMs = toMillis(
    shift?.completedAt || shift?.endTime || shift?.end
  );

  const ms = Number.isFinite(startMs) ? startMs : endMs;

  if (!Number.isFinite(ms)) return false;

  const bounds = regionalCalendarBounds(nowMs, timeZone);
  const {today, tomorrow, afterTomorrow, weekStart, weekEnd, monthStart, monthEnd} = Object.fromEntries(Object.entries(bounds).map(([key,value]) => [key,new Date(value)]));

  const overlaps = (from, to) => {
    if (Number.isFinite(startMs) && Number.isFinite(endMs)) {
      return endMs > startMs && startMs < to && endMs > from;
    }
    return ms >= from && ms < to;
  };

  if (scope === "today") {
    return overlaps(today.getTime(), tomorrow.getTime());
  }

  if (scope === "tomorrow") {
    return overlaps(tomorrow.getTime(), afterTomorrow.getTime());
  }

  if (scope === "week") {
    return overlaps(weekStart.getTime(), weekEnd.getTime());
  }

  if (scope === "month") {
    return overlaps(monthStart.getTime(), monthEnd.getTime());
  }

  if (scope === "history") {
    return Number.isFinite(endMs) ? endMs <= today.getTime() : ms < today.getTime();
  }

  return true;
}

function MetricCard({
  label,
  value,
  tone = "slate",
  wide = false,
}) {
  const tones = {
    blue: ["#eff6ff", "#bfdbfe", "#1d4ed8"],
    cyan: ["#ecfeff", "#a5f3fc", "#0891b2"],
    green: ["#ecfdf5", "#a7f3d0", "#047857"],
    amber: ["#fff7ed", "#fed7aa", "#b45309"],
    red: ["#fff1f2", "#fecdd3", "#be123c"],
    violet: ["#f5f3ff", "#ddd6fe", "#6d28d9"],
    slate: ["#f8fafc", "#e2e8f0", "#0f172a"],
  };

  const [background, border, color] =
    tones[tone] || tones.slate;

  return (
    <div
      style={{
        gridColumn: wide ? "1 / -1" : "auto",
        borderRadius: 18,
        padding: 14,
        background,
        border: `1px solid ${border}`,
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 900,
          letterSpacing: ".08em",
          textTransform: "uppercase",
          color: "#64748b",
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 8,
          fontSize: 26,
          lineHeight: 1,
          fontWeight: 950,
          color,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 20,
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: 16,
          fontWeight: 950,
          color: "#0f172a",
        }}
      >
        {title}
      </div>

      {subtitle ? (
        <div
          style={{
            marginTop: 4,
            marginBottom: 14,
            fontSize: 12,
            fontWeight: 700,
            color: "#64748b",
          }}
        >
          {subtitle}
        </div>
      ) : (
        <div style={{ height: 12 }} />
      )}

      {children}
    </section>
  );
}

export default function FacilityPulseMobile({ profile }) {
  const tenantId = String(
    profile?.tenantId ||
      profile?.tenantKey ||
      ""
  )
    .trim()
    .toLowerCase();

  const tenantTimeZone = useTenantTimeZone(tenantId);

  const facilityId = String(
    profile?.facilityId ||
      profile?.primaryFacilityId ||
      profile?.facilityIds?.[0] ||
      ""
  )
    .trim()
    .toLowerCase();

  console.log("FACILITY PULSE SCOPE", {
    uid: profile?.uid,
    email: profile?.email,
    role: profile?.role,
    platformRole: profile?.platformRole,
    tenantId,
    facilityId,
    profileFacilityId: profile?.facilityId,
    profilePrimaryFacilityId:
      profile?.primaryFacilityId,
    profileFacilityIds:
      profile?.facilityIds,
  });

  const [errors, setErrors] = useState({});
const [activeTab, setActiveTab] = useState("executive");

/*
 * =========================================================
 * CANONICAL FACILITY DATA
 * =========================================================
 *
 * Facility Pulse must consume the same operational facility
 * data contracts as Facility Planner.
 *
 * No independent facilityProfiles/users/shifts listeners
 * should exist in this page.
 */

const facilityDataEnabled =
  Boolean(tenantId && facilityId);

const {
  facilityProfile: facilityProfileData,
  error: facilityProfileDataError,
  loading: facilityProfileLoading,
} = useFacilityProfile({
  tenantId,
  facilityId,

  enabled: facilityDataEnabled,

  debugLabel:
    "FACILITY PULSE PROFILE",
});

const {
  workers: facilityWorkers,
  facilityUsers,
  roster: facilityRoster,
  error: facilityWorkersDataError,
  loading: facilityWorkersLoading,
} = useFacilityWorkers({
  tenantId,
  facilityId,

  enabled: facilityDataEnabled,
});

const {
  shifts: facilityShifts,
  error: shiftsDataError,
  loading: facilityShiftsLoading,
} = useFacilityShifts({
  tenantId,
  facilityId,

  allowedFacilityIds:
    facilityId
      ? [facilityId]
      : [],

  enabled: facilityDataEnabled,

  debugLabel:
    "FACILITY PULSE SHIFTS",
});

/*
 * Preserve the existing Facility Pulse variable names
 * so the metrics/UI below do not need to be rewritten.
 */
const facilityProfile =
  facilityProfileData || {};

const shifts =
  Array.isArray(facilityShifts)
    ? facilityShifts
    : [];

const users =
  Array.isArray(facilityUsers)
    ? facilityUsers
    : [];

  const [pulseScope, setPulseScope] = useState(
    () =>
      localStorage.getItem("shiftbridge_pulse_scope") ||
      "today"
  );

 /*
 * =========================================================
 * CANONICAL DATA ERROR BRIDGE
 * =========================================================
 *
 * Keep the existing Facility Pulse error presentation
 * while the underlying data now comes from shared hooks.
 */
useEffect(() => {
  setErrors((current) => ({
    ...current,

    account:
      !tenantId
        ? "This facility account is missing a tenant assignment."
        : !facilityId
        ? "This facility account is not assigned to a facility."
        : "",

    profile:
      facilityProfileDataError
        ? "Facility profile could not be loaded."
        : "",

    users:
      facilityWorkersDataError
        ? "Facility workforce could not be loaded."
        : "",

    shifts:
      shiftsDataError
        ? "Facility shifts could not be loaded."
        : "",
  }));
}, [
  tenantId,
  facilityId,
  facilityProfileDataError,
  facilityWorkersDataError,
  shiftsDataError,
]);

useEffect(() => {
  console.log(
    "FACILITY PULSE CANONICAL DATA",
    {
      tenantId,
      facilityId,

      facilityProfileLoaded:
        Boolean(facilityProfileData),

      facilityUsers:
        facilityUsers?.length || 0,

      facilityWorkers:
        facilityWorkers?.length || 0,

      roster:
        facilityRoster?.length || 0,

      shifts:
        facilityShifts?.length || 0,

      loading: {
        profile:
          facilityProfileLoading,

        workers:
          facilityWorkersLoading,

        shifts:
          facilityShiftsLoading,
      },
    }
  );
}, [
  tenantId,
  facilityId,
  facilityProfileData,
  facilityUsers,
  facilityWorkers,
  facilityRoster,
  facilityShifts,
  facilityProfileLoading,
  facilityWorkersLoading,
  facilityShiftsLoading,
]);

  const scopeNow = useOperationalClock();

  const scopedShifts = useMemo(
    () =>
      shifts.filter((shift) =>
        isInScope(shift, pulseScope, scopeNow, resolveShiftTimeZone(shift, facilityProfile, {timeZone: tenantTimeZone}))
      ),
    [shifts, pulseScope, scopeNow, facilityProfile, tenantTimeZone]
  );

  /*
   * scopedShifts has already been filtered
   * by the selected Facility Pulse scope:
   * Today / Tomorrow / Week / Month / History.
   *
   * Do not apply another Today-only filter here.
   */
  const operational = useMemo(
    () =>
      buildShiftMetrics(
        scopedShifts
      ),
    [scopedShifts]
  );

  const financial = useMemo(
    () =>
      buildFacilityFinancialMetrics(scopedShifts, {
        facilityRates:
          facilityProfile?.billingRates || profile?.billingRates || {},
      }),
    [scopedShifts, facilityProfile, profile]
  );

  const credentialSummary = useMemo(
    () => buildCredentialExpirationSummary(users),
    [users]
  );

  const visibilityRows =
    scopedShifts.map((shift) => ({
      shift,
      summary:
        getShiftOperationalSummary(
          shift, scopeNow
        ),
    }));

  const activeShifts =
    visibilityRows
      .filter(
        ({ summary }) =>
          !CLOSED_STATUSES.includes(
            summary.status
          )
      )
      .map(
        ({ shift }) =>
          shift
      );

  const totalShifts =
    Number(
      operational.total || 0
    );

  const coveredCount =
    Number(
      operational.covered || 0
    );

  const openCount =
    Number(
      operational.open || 0
    ) +
    Number(
      operational.offered || 0
    ) +
    Number(
      operational.requested || 0
    ) +
    Number(
      operational.timeRequired || 0
    ) +
    Number(
      operational.awaitingAcceptance || 0
    );

  const completedCount =
    Number(
      operational.completed || 0
    );

  const fillRate =
    totalShifts > 0
      ? Number(operational.coverage || 0)
      : null;

  const totalHours = Number(operational.actualHours || 0);

  const pulseScore =
    totalShifts > 0
      ? Math.max(
          0,
          Math.min(
            100,
            100 -
              openCount * 10 -
              (fillRate < 90 ? 10 : 0) -
              (fillRate < 80 ? 10 : 0)
          )
        )
      : null;

  const pulseLabel =
    pulseScore === null
      ? "No Scheduled Shifts"
      : pulseScore >= 90
      ? "Healthy"
      : pulseScore >= 75
      ? "Watch"
      : "Critical";

  const pulseTone =
    pulseScore === null
      ? "amber"
      : pulseScore >= 90
      ? "green"
      : pulseScore >= 75
      ? "amber"
      : "red";

  const facilityName =
    facilityProfile?.facilityName ||
    facilityProfile?.name ||
    profile?.facilityName ||
    profile?.name ||
    facilityId ||
    "Facility";

  const targetMap =
    facilityProfile?.financialTargets || {};

  const targetValue = pulseScope === "week" ? targetMap.weekly : pulseScope === "month" ? targetMap.monthly : targetMap.daily;
  const budgetTarget = pulseScope === "history" || targetValue == null || targetValue === "" || !Number.isFinite(Number(targetValue)) ? null : Number(targetValue);
  const variance = budgetTarget === null || !financial.billingDataComplete ? null : Number(financial.actualSpend || 0) - budgetTarget;
  const projectedVariance = budgetTarget === null || !financial.billingDataComplete ? null : Number(financial.projectedSpend || 0) - budgetTarget;

  const roleRows = [
    "RN",
    "LVN",
    "CNA",
    "MED_AIDE",
    "CAREGIVER",
  ].map((role) => {
    const matching =
      visibilityRows.filter(
        ({ shift, summary }) =>
          normalizeRole(shift) ===
            role &&
          ![
            "cancelled",
            "canceled",
          ].includes(
            summary.status
          )
      );

    const filled =
      matching.filter(
        ({ summary }) =>
          summary
            .confirmedCoverage
      ).length;

    return {
      role,
      total:
        matching.length,

      filled,

      gap:
        matching.length -
        filled,
    };
  });

  const visibleRoleRows = roleRows.filter(
    (row) => row.total > 0
  );

   const confirmedWorkerIds =
    new Set(
      visibilityRows
        .filter(
          ({ summary }) =>
            summary
              .confirmedCoverage
        )
        .map(
          ({ summary }) =>
            summary
              .assignedWorkerUid
        )
        .filter(Boolean)
    );

  const readyWorkers = users.filter((user) =>
    ["active", "approved", "ready"].includes(
      String(
        user.workerStatus ||
          user.status ||
          user.onboardingStatus ||
          ""
      ).toLowerCase()
    )
  ).length;

  const onShift =
    visibilityRows.filter(
      ({ summary }) =>
summary.displayState === "on_shift"
    ).length;

  const alerts = [
    [
      "Open critical shifts",
      openCount,
      openCount ? "red" : "green",
    ],
    [
      "Late arrivals",
      Number(operational.lateArrivals || 0),
      operational.lateArrivals ? "amber" : "green",
    ],
    [
      "No shows",
      Number(operational.noShows || 0),
      operational.noShows ? "red" : "green",
    ],
    [
      "Expired credentials",
      Number(credentialSummary.expired || 0),
      credentialSummary.expired ? "red" : "green",
    ],
    [
      "Credentials expiring soon",
      Number(credentialSummary.critical || 0) +
        Number(credentialSummary.warning || 0),
      credentialSummary.critical ||
      credentialSummary.warning
        ? "amber"
        : "green",
    ],
  ];

  const timezoneUnavailable = shifts.some(shift => !resolveShiftTimeZone(shift, facilityProfile, {timeZone: tenantTimeZone}));

  const errorMessage = Object.values(errors)
    .filter(Boolean)
    .join(" ");

  const setScope = (scope) => {
    localStorage.setItem(
      "shiftbridge_pulse_scope",
      scope
    );

    setPulseScope(scope);
  };

  if (timezoneUnavailable) return <p role="alert">Timezone unavailable. Configure the facility or tenant timezone before viewing operational totals.</p>;

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(145deg,#eef5ff 0%,#f8fafc 45%,#ecfeff 100%)",
        padding: "14px 12px 28px",
        fontFamily:
          "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <main
        style={{
          width: "100%",
          maxWidth: 620,
          margin: "0 auto",
        }}
      >
        <header style={{ padding: "10px 4px 18px" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "7px 11px",
              borderRadius: 999,
              background: "#e0f2fe",
              border: "1px solid #bae6fd",
              color: "#0369a1",
              fontSize: 12,
              fontWeight: 900,
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: 999,
                background: "#0ea5e9",
              }}
            />

            ShiftBridge Enterprise
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              gap: 12,
              marginTop: 12,
            }}
          >
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: "clamp(28px,8vw,40px)",
                  lineHeight: 1,
                  letterSpacing: "-.04em",
                  color: "#0f172a",
                }}
              >
                Facility Pulse
              </h1>

              <div
                style={{
                  marginTop: 7,
                  color: "#475569",
                  fontSize: 13,
                  fontWeight: 750,
                }}
              >
                {facilityName} · Executive Visibility Snapshot
              </div>
            </div>

            <button
              type="button"
              onClick={() => signOut(auth)}
              aria-label="Log out"
              style={{
                flex: "0 0 auto",
                padding: "9px 12px",
                borderRadius: 12,
                border: "1px solid #cbd5e1",
                background: "rgba(255,255,255,.8)",
                color: "#334155",
                fontWeight: 850,
                cursor: "pointer",
              }}
            >
              Logout
            </button>
          </div>
        </header>

        <ShiftBridgeAssist
          title="ShiftBridge Assist — Facility Visibility"
          steps={[
            "Review current facility health.",
            "Check open shifts and coverage gaps.",
            "Monitor completed work and actual hours.",
            "Review facility spend without exposing worker pay rates.",
          ]}
          note="Facility Pulse is visibility-only and never shows worker pay rates."
        />

<section
  style={{
    marginTop: 14,
    padding: "14px 16px",
    borderRadius: 18,
    background: "rgba(255,255,255,.72)",
    border: "1px solid #dbe4ee",
  }}
>
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 16,
    }}
  >
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 900,
          letterSpacing: ".1em",
          color: "#64748b",
          textTransform: "uppercase",
        }}
      >
        Operational Scope
      </div>

      <div
        style={{
          marginTop: 6,
          fontSize: 17,
          fontWeight: 950,
          color: "#0f172a",
          textTransform: "uppercase",
        }}
      >
        {SCOPES.find(([key]) => key === pulseScope)?.[1] || "Today"}
      </div>

      <div
        style={{
          marginTop: 5,
          fontSize: 11,
          color: "#64748b",
          fontWeight: 700,
        }}
      >
        Source: Facility Planner
      </div>
    </div>

    <select
      value={pulseScope}
      onChange={(event) => setScope(event.target.value)}
      aria-label="Select operational scope"
      style={{
        width: 118,
        height: 44,
        padding: "0 12px",
        borderRadius: 13,
        border: "1px solid #cbd5e1",
        background: "#ffffff",
        color: "#334155",
        fontSize: 12,
        fontWeight: 900,
        cursor: "pointer",
        outline: "none",
      }}
    >
      {SCOPES.map(([key, label]) => (
        <option key={key} value={key}>
          {label}
        </option>
      ))}
    </select>
  </div>
</section>

<nav
  aria-label="Facility Pulse sections"
  style={{
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: 14,
    padding: 5,
    overflowX: "auto",
    borderRadius: 14,
    background: "rgba(255,255,255,.72)",
    border: "1px solid #dbe4ee",
    scrollbarWidth: "none",
  }}
>
  {TABS.map(([key, label]) => {
    const selected = activeTab === key;

    return (
      <button
        key={key}
        type="button"
        onClick={() => setActiveTab(key)}
        aria-current={selected ? "page" : undefined}
        style={{
          flex: "1 0 auto",
          minWidth: 82,
          padding: "9px 12px",
          borderRadius: 10,
          border: selected
            ? "1px solid #2563eb"
            : "1px solid transparent",
          background: selected ? "#2563eb" : "transparent",
          color: selected ? "#ffffff" : "#475569",
          fontSize: 11,
          fontWeight: 900,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </button>
    );
  })}
</nav>

<div
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 10,
    marginBottom: 12,
    padding: "10px 12px",
    borderRadius: 14,
    background: "rgba(255,255,255,.72)",
    border: "1px solid #dbe4ee",
  }}
>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8,
    }}
  >
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        color: "#047857",
        fontSize: 10,
        fontWeight: 950,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: 999,
          background: "#10b981",
          boxShadow: "0 0 0 3px #d1fae5",
        }}
      />

      LIVE
    </span>

    <span
      style={{
        width: 1,
        height: 18,
        background: "#cbd5e1",
      }}
    />

    <span
      style={{
        color:
          pulseTone === "green"
            ? "#047857"
            : pulseTone === "red"
            ? "#be123c"
            : "#b45309",
        fontSize: 10,
        fontWeight: 950,
      }}
    >
      {pulseLabel}
    </span>
  </div>

  <div
    style={{
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 8,
      color: "#475569",
      fontSize: 10,
      fontWeight: 850,
    }}
  >
    <span>
      Coverage:{" "}
      <strong style={{ color: "#0f172a" }}>
        {fillRate === null ? "N/A" : `${fillRate}%`}
      </strong>
    </span>

    <span style={{ color: "#cbd5e1" }}>•</span>

    <span>
      Open:{" "}
      <strong
        style={{
          color: openCount ? "#be123c" : "#047857",
        }}
      >
        {openCount}
      </strong>
    </span>
  </div>
</div>

        {errorMessage ? (
          <div
            role="alert"
            style={{
              marginBottom: 12,
              padding: 13,
              borderRadius: 15,
              background: "#fff1f2",
              border: "1px solid #fecdd3",
              color: "#9f1239",
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            {errorMessage}
          </div>
        ) : null}

        <div
          style={{
            padding: 15,
            borderRadius: 24,
            background: "rgba(255,255,255,.96)",
            border: "1px solid rgba(255,255,255,.8)",
            boxShadow:
              "0 24px 60px rgba(15,23,42,.13)",
          }}
        >
          <div
            style={{
              height: 4,
              margin: "-15px -15px 15px",
              borderRadius: "24px 24px 0 0",
              background:
                "linear-gradient(90deg,#2563eb,#06b6d4,#14b8a6)",
            }}
          />

{activeTab === "executive" ? (
  <div style={{ display: "grid", gap: 12 }}>
    <Section
      title="Executive Overview"
      subtitle={`${
        SCOPES.find(([key]) => key === pulseScope)?.[1] || "Today"
      } operational health at a glance`}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2,minmax(0,1fr))",
          gap: 10,
        }}
      >
        {[
          {
            label: "Pulse Score",
            value: pulseScore ?? "N/A",
            color:
              pulseTone === "green"
                ? "#047857"
                : pulseTone === "red"
                ? "#be123c"
                : "#b45309",
          },
          {
            label: "Coverage",
            value: fillRate === null ? "N/A" : `${fillRate}%`,
            color: "#0369a1",
          },
          {
            label: "Open Shifts",
            value: openCount,
            color: openCount ? "#be123c" : "#047857",
          },
          {
            label: "Completed",
            value: completedCount,
            color: "#047857",
          },
        ].map((item) => (
          <div
            key={item.label}
            style={{
              minWidth: 0,
              padding: "13px 14px",
              borderRadius: 14,
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
            }}
          >
            <div
              style={{
                fontSize: 9,
                lineHeight: 1.2,
                fontWeight: 950,
                letterSpacing: ".07em",
                textTransform: "uppercase",
                color: "#64748b",
              }}
            >
              {item.label}
            </div>

            <div
              style={{
                marginTop: 7,
                fontSize: 24,
                lineHeight: 1,
                fontWeight: 950,
                color: item.color,
              }}
            >
              {item.value}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: 10,
          padding: "13px 14px",
          borderRadius: 14,
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 9,
                fontWeight: 950,
                letterSpacing: ".07em",
                textTransform: "uppercase",
                color: "#64748b",
              }}
            >
              Actual Hours
            </div>

            <div
              style={{
                marginTop: 7,
                fontSize: 24,
                lineHeight: 1,
                fontWeight: 950,
                color: "#0f172a",
              }}
            >
              {totalHours.toFixed(1)}
            </div>
          </div>

          <div
            style={{
              color: "#64748b",
              fontSize: 10,
              fontWeight: 800,
              textAlign: "right",
            }}
          >
            Completed shift hours
          </div>
        </div>
      </div>
    </Section>

    <Section
      title="Coverage Performance"
      subtitle="Filled shifts compared with scheduled demand"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          color: "#334155",
          fontSize: 11,
          fontWeight: 900,
        }}
      >
        <span>Overall Coverage</span>

        <span style={{ color: "#0f172a" }}>
          {fillRate === null ? "N/A" : `${fillRate}%`}
        </span>
      </div>

      <div
        style={{
          height: 8,
          marginTop: 9,
          borderRadius: 999,
          background: "#e2e8f0",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${fillRate === null ? 0 : Math.min(100, fillRate)}%`,
            height: "100%",
            borderRadius: 999,
            background:
              fillRate === null
                ? "#cbd5e1"
                : fillRate >= 90
                ? "#10b981"
                : fillRate >= 75
                ? "#f59e0b"
                : "#ef4444",
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          marginTop: 8,
          color: "#64748b",
          fontSize: 10,
          fontWeight: 750,
        }}
      >
                  <span>
                    {coveredCount} confirmed
                  </span>

                  <span>
                    {totalShifts} scheduled
                  </span>
      </div>
    </Section>
  </div>
) : null}

{activeTab === "workforce" ? (
  <div style={{ display: "grid", gap: 12 }}>
    <Section
      title="Workforce Visibility"
      subtitle="Ready staff and active deployment"
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2,minmax(0,1fr))",
          gap: 10,
        }}
      >
        {[
          {
            label: "Ready Workers",
            value: readyWorkers,
            color: "#1d4ed8",
          },
                    {
                      label: "Confirmed",
                      value:
                        confirmedWorkerIds.size,
                      color: "#0891b2",
                    },
          {
            label: "On Shift",
            value: onShift,
            color: "#6d28d9",
          },
          {
            label: "Completed",
            value: completedCount,
            color: "#047857",
          },
        ].map((item) => (
          <div
            key={item.label}
            style={{
              minWidth: 0,
              padding: "13px 14px",
              borderRadius: 14,
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
            }}
          >
            <div
              style={{
                fontSize: 9,
                lineHeight: 1.2,
                fontWeight: 950,
                letterSpacing: ".07em",
                textTransform: "uppercase",
                color: "#64748b",
              }}
            >
              {item.label}
            </div>

            <div
              style={{
                marginTop: 7,
                fontSize: 24,
                lineHeight: 1,
                fontWeight: 950,
                color: item.color,
              }}
            >
              {item.value}
            </div>
          </div>
        ))}
      </div>
    </Section>

    <Section
      title="Role Coverage"
      subtitle="Available staffing against scheduled demand"
    >
      {visibleRoleRows.length ? (
        <div style={{ display: "grid", gap: 13 }}>
          {visibleRoleRows.map((row) => {
            const percentage = row.total
              ? Math.round((row.filled / row.total) * 100)
              : 0;

            return (
              <div
                key={row.role}
                style={{
                  paddingBottom: 12,
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    fontSize: 11,
                    fontWeight: 900,
                    color: "#334155",
                  }}
                >
                  <span>{row.role.replace("_", " ")}</span>

                  <span
                    style={{
                      color: row.gap ? "#b45309" : "#047857",
                    }}
                  >
                    {row.filled}/{row.total} filled
                  </span>
                </div>

                <div
                  style={{
                    height: 7,
                    marginTop: 8,
                    borderRadius: 999,
                    background: "#e2e8f0",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(100, percentage)}%`,
                      height: "100%",
                      borderRadius: 999,
                      background:
                        percentage === 100
                          ? "#10b981"
                          : percentage >= 75
                          ? "#f59e0b"
                          : "#ef4444",
                    }}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    marginTop: 7,
                    color: "#64748b",
                    fontSize: 10,
                    fontWeight: 750,
                  }}
                >
                  <span>{percentage}% coverage</span>

                  <span>
                    {row.gap
                      ? `${row.gap} open`
                      : "Fully covered"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            padding: 14,
            borderRadius: 12,
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            color: "#64748b",
            fontSize: 11,
            fontWeight: 800,
          }}
        >
          No scheduled workforce demand in this scope.
        </div>
      )}
    </Section>
  </div>
) : null}

{activeTab === "shifts" ? (
  <div style={{ display: "grid", gap: 12 }}>
    <Section
      title="Shift Schedule"
      subtitle="Current facility shifts for the selected period"
    >
      {scopedShifts.length ? (
        <div style={{ display: "grid", gap: 9 }}>
          {[...scopedShifts]
            .sort(
              (firstShift, secondShift) =>
                Number(
                  firstShift.startTime ||
                    firstShift.startMs ||
                    firstShift.dateMs ||
                    0
                ) -
                Number(
                  secondShift.startTime ||
                    secondShift.startMs ||
                    secondShift.dateMs ||
                    0
                )
            )
            .map((shift, index) => {
              const shiftVisibility =
  getShiftOperationalSummary(
    shift, scopeNow
  );

const status =
  shiftVisibility.status;

const statusLabel =
  shiftVisibility
    .operationalLabel;

              const role = normalizeRole(shift).replace(
                "_",
                " "
              );

              const startValue =
                shift.startTime ||
                shift.startMs ||
                shift.dateMs;

              const endValue =
                shift.endTime || shift.endMs;

              const startDate = startValue
                ? new Date(toMillis(startValue))
                : null;

              const endDate = endValue
                ? new Date(toMillis(endValue))
                : null;

              const validStart =
                startDate &&
                !Number.isNaN(startDate.getTime());

              const validEnd =
                endDate &&
                !Number.isNaN(endDate.getTime());

              const timeZone = resolveShiftTimeZone(shift, facilityProfile, {timeZone: tenantTimeZone});
              const dateLabel = !timeZone ? "Timezone unavailable" : validStart
                ? startDate.toLocaleDateString([], {timeZone,
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })
                : shift.date || "Date unavailable";

              const timeLabel = !timeZone ? "Timezone unavailable" : validStart
                ? `${startDate.toLocaleTimeString([], {timeZone,
                    hour: "numeric",
                    minute: "2-digit",
                  })}${
                    validEnd
                      ? ` – ${endDate.toLocaleTimeString([], {timeZone,
                          hour: "numeric",
                          minute: "2-digit",
                        })}`
                      : ""
                  }`
                : shift.timeLabel ||
                  shift.shiftTime ||
                  "Time unavailable";

              const workerName = resolveAssignedWorkerName(
                shift, shiftVisibility.assignedWorkerUid, facilityWorkers
              );
const statusColors =
  [
    "completed",
    "arrived",
  ].includes(status)
    ? {
        background:
          "#ecfdf5",
        border:
          "#a7f3d0",
        text:
          "#047857",
      }
    : status ===
      "tracking"
    ? {
        background:
          "#ecfeff",
        border:
          "#a5f3fc",
        text:
          "#0e7490",
      }
    : shiftVisibility
        .awaitingAcceptance
    ? {
        background:
          "#fff7ed",
        border:
          "#fed7aa",
        text:
          "#9a3412",
      }
    : [
        "assigned",
        "activated",
      ].includes(status)
    ? {
        background:
          "#eff6ff",
        border:
          "#bfdbfe",
        text:
          "#1d4ed8",
      }
    : [
        "open",
        "offered",
        "requested",
        "time_required",
      ].includes(status)
    ? {
        background:
          "#fff1f2",
        border:
          "#fecdd3",
        text:
          "#be123c",
      }
    : {
        background:
          "#f8fafc",
        border:
          "#e2e8f0",
        text:
          "#475569",
      };

              return (
                <div
                  key={shift.id || `${dateLabel}-${role}-${index}`}
                  style={{
                    padding: "12px 13px",
                    borderRadius: 13,
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 7,
                        }}
                      >
                        <span
                          style={{
                            color: "#0f172a",
                            fontSize: 12,
                            fontWeight: 950,
                          }}
                        >
                          {role}
                        </span>

                        <span
                          style={{
                            color: "#cbd5e1",
                            fontSize: 10,
                          }}
                        >
                          •
                        </span>

                        <span
                          style={{
                            color: "#475569",
                            fontSize: 11,
                            fontWeight: 850,
                          }}
                        >
                          {dateLabel}
                        </span>
                      </div>

                      <div
                        style={{
                          marginTop: 5,
                          color: "#334155",
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        {timeLabel}
                      </div>

                      <div
                        style={{
                          marginTop: 5,
                          color: workerName
                            ? "#475569"
                            : "#be123c",
                          fontSize: 10,
                          fontWeight: 750,
                        }}
                      >
                        {workerName
                          ? `Worker: ${workerName}`
                          : status === "open"
                          ? "Worker needed"
                          : "Worker assignment unavailable"}
                      </div>
                    </div>

                    <span
                      style={{
                        flex: "0 0 auto",
                        padding: "6px 9px",
                        borderRadius: 999,
                        background: statusColors.background,
                        border: `1px solid ${statusColors.border}`,
                        color: statusColors.text,
                        fontSize: 9,
                        fontWeight: 950,
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {statusLabel}
                    </span>
                  </div>
                </div>
              );
            })}
        </div>
      ) : (
        <div
          style={{
            padding: 14,
            borderRadius: 12,
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            color: "#64748b",
            fontSize: 11,
            fontWeight: 800,
          }}
        >
          No shifts scheduled for this scope.
        </div>
      )}
    </Section>

    <Section
      title="Role Coverage"
      subtitle="Filled shifts against scheduled demand"
    >
      {visibleRoleRows.length ? (
        <div style={{ display: "grid", gap: 13 }}>
          {visibleRoleRows.map((row) => {
            const percentage = row.total
              ? Math.round((row.filled / row.total) * 100)
              : 0;

            return (
              <div
                key={row.role}
                style={{
                  paddingBottom: 12,
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    color: "#334155",
                    fontSize: 11,
                    fontWeight: 900,
                  }}
                >
                  <span>{row.role.replace("_", " ")}</span>

                  <span>
                    {row.filled}/{row.total} filled
                  </span>
                </div>

                <div
                  style={{
                    height: 7,
                    marginTop: 8,
                    borderRadius: 999,
                    background: "#e2e8f0",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(100, percentage)}%`,
                      height: "100%",
                      borderRadius: 999,
                      background:
                        percentage === 100
                          ? "#10b981"
                          : percentage >= 75
                          ? "#f59e0b"
                          : "#ef4444",
                    }}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    marginTop: 7,
                    color: "#64748b",
                    fontSize: 10,
                    fontWeight: 750,
                  }}
                >
                  <span>{percentage}% coverage</span>

                  <span>
                    {row.gap
                      ? `${row.gap} open`
                      : "Fully covered"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            padding: 14,
            borderRadius: 12,
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            color: "#64748b",
            fontSize: 11,
            fontWeight: 800,
          }}
        >
          No scheduled role demand in this scope.
        </div>
      )}
    </Section>
  </div>
) : null}

{activeTab === "financial" ? (
  <div style={{ display: "grid", gap: 12 }}>
    <Section
      title="Financial Visibility"
      subtitle="Facility billing view; worker pay rates remain private"
    >
      {!financial.billingDataComplete && (
        <p role="status">Billing information is unavailable or incomplete for this scope. Spend totals are not confirmed.</p>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2,minmax(0,1fr))",
          gap: 10,
        }}
      >
        {[
          {
            label: "Budget Target",
            value: budgetTarget !== null ? money(budgetTarget) : "N/A",
            color: "#1d4ed8",
          },
          {
            label: "Actual Spend",
            value: totalShifts && financial.billingDataComplete
              ? money(financial.actualSpend)
              : "Unavailable",
            color: "#047857",
          },
          { label: "Projected Spend", value: totalShifts && financial.billingDataComplete ? money(financial.projectedSpend) : "Unavailable", color: "#1d4ed8" },
          { label: "Projected Staffing Hours", value: totalShifts ? Number(financial.projectedHours || 0).toLocaleString() : "N/A", color: "#1d4ed8" },
          { label: "Projected Variance", value: projectedVariance !== null ? money(projectedVariance) : "N/A", color: projectedVariance > 0 ? "#b45309" : "#047857" },
        ].map((item) => (
          <div
            key={item.label}
            style={{
              minWidth: 0,
              padding: "13px 14px",
              borderRadius: 14,
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
            }}
          >
            <div
              style={{
                fontSize: 9,
                lineHeight: 1.2,
                fontWeight: 950,
                letterSpacing: ".07em",
                textTransform: "uppercase",
                color: "#64748b",
              }}
            >
              {item.label}
            </div>

            <div
              style={{
                marginTop: 7,
                fontSize: 21,
                lineHeight: 1.1,
                fontWeight: 950,
                color: item.color,
                overflowWrap: "anywhere",
              }}
            >
              {item.value}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: 10,
          padding: "13px 14px",
          borderRadius: 14,
          background: variance > 0 ? "#fff7ed" : "#ecfdf5",
          border:
            variance > 0
              ? "1px solid #fed7aa"
              : "1px solid #a7f3d0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 9,
                fontWeight: 950,
                letterSpacing: ".07em",
                textTransform: "uppercase",
                color: "#64748b",
              }}
            >
              Actual Variance
            </div>

            <div
              style={{
                marginTop: 7,
                fontSize: 23,
                lineHeight: 1,
                fontWeight: 950,
                color: variance > 0 ? "#b45309" : "#047857",
              }}
            >
              {totalShifts && variance !== null ? money(variance) : "N/A"}
            </div>
          </div>

          <div
            style={{
              maxWidth: 120,
              color: variance > 0 ? "#92400e" : "#065f46",
              fontSize: 10,
              lineHeight: 1.4,
              fontWeight: 850,
              textAlign: "right",
            }}
          >
            {budgetTarget === null
              ? "Budget target unavailable for this scope"
              : !totalShifts
              ? "No scheduled billing activity"
              : variance > 0
              ? "Above budget target"
              : variance < 0
              ? "Below budget target"
              : "On budget target"}
          </div>
        </div>
      </div>
    </Section>

    <Section
      title="Financial Safeguard"
      subtitle="Facility-authorized billing information only"
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          padding: "12px 13px",
          borderRadius: 13,
          background: "#eff6ff",
          border: "1px solid #bfdbfe",
          color: "#1e40af",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            flex: "0 0 auto",
            fontSize: 16,
            lineHeight: 1,
          }}
        >
          🔒
        </span>

        <div
          style={{
            fontSize: 10,
            lineHeight: 1.55,
            fontWeight: 800,
          }}
        >
          Worker pay rates, payroll totals, margins, and internal financial
          details are not displayed in Facility Pulse.
        </div>
      </div>
    </Section>
  </div>
) : null}

{activeTab === "alerts" ? (
  <div style={{ display: "grid", gap: 12 }}>
    <Section
      title="Alerts & Risk"
      subtitle="Operational and credential exceptions"
    >
      <div style={{ display: "grid", gap: 9 }}>
        {alerts.map(([label, value, tone]) => {
          const palette =
            tone === "red"
              ? {
                  background: "#fff1f2",
                  border: "#fecdd3",
                  text: "#be123c",
                  dot: "#e11d48",
                }
              : tone === "amber"
              ? {
                  background: "#fff7ed",
                  border: "#fed7aa",
                  text: "#b45309",
                  dot: "#f59e0b",
                }
              : {
                  background: "#ecfdf5",
                  border: "#a7f3d0",
                  text: "#047857",
                  dot: "#10b981",
                };

          return (
            <div
              key={label}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 13px",
                borderRadius: 13,
                background: palette.background,
                border: `1px solid ${palette.border}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                  gap: 10,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    flex: "0 0 auto",
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    background: palette.dot,
                  }}
                />

                <span
                  style={{
                    color: palette.text,
                    fontSize: 11,
                    fontWeight: 900,
                  }}
                >
                  {label}
                </span>
              </div>

              <span
                style={{
                  flex: "0 0 auto",
                  minWidth: 28,
                  color: palette.text,
                  fontSize: 18,
                  lineHeight: 1,
                  fontWeight: 950,
                  textAlign: "right",
                }}
              >
                {value}
              </span>
            </div>
          );
        })}
      </div>
    </Section>

    <Section
      title="Risk Summary"
      subtitle="Current exceptions requiring facility attention"
    >
      <div
        style={{
          padding: "12px 13px",
          borderRadius: 13,
          background: alerts.some(
            ([, value, tone]) =>
              Number(value) > 0 &&
              (tone === "red" || tone === "amber")
          )
            ? "#fff7ed"
            : "#ecfdf5",
          border: alerts.some(
            ([, value, tone]) =>
              Number(value) > 0 &&
              (tone === "red" || tone === "amber")
          )
            ? "1px solid #fed7aa"
            : "1px solid #a7f3d0",
          color: alerts.some(
            ([, value, tone]) =>
              Number(value) > 0 &&
              (tone === "red" || tone === "amber")
          )
            ? "#92400e"
            : "#065f46",
          fontSize: 10,
          lineHeight: 1.55,
          fontWeight: 800,
        }}
      >
        {alerts.some(
          ([, value, tone]) =>
            Number(value) > 0 &&
            (tone === "red" || tone === "amber")
        )
          ? "One or more operational or credential exceptions require review."
          : "No active operational or credential risks require attention."}
      </div>
    </Section>
  </div>
) : null}
        </div>
      </main>
    </div>
  );
}
